import React, { useEffect, useState } from 'react';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import { useDocumentUpload } from 'src/pages/SharedPageCustomHooks/UseDocumentUpload/useDocumentUpload';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import DocumentTypeSelector from './DocumentTypeSelector';
import DocumentUploadFields from './DocumentUploadFields';
import './ModernizedDocumentForm.scss';

interface Props {
  saving: boolean;
  onSave: (formData: DocumentFormData) => Promise<void>;
  onClose: () => void;
  from?: string;
}

const stripFileExtension = (filename: string): string => {
  const lastDot: number = filename.lastIndexOf('.');
  return lastDot === -1 ? filename : filename.slice(0, lastDot);
};

const ModernizedDocumentForm: React.FC<Props> = ({
  saving,
  onSave,
  onClose,
  from,
}) => {
  const [formData, setFormData] = useState<DocumentFormData>({
    docAvailability: false,
  });

  const updateFormData = (data: Partial<DocumentFormData>) => {
    setFormData((prev: DocumentFormData) => ({ ...prev, ...data }));
  };

  const { errorMessage, validateAndProcessFile, clearError } =
    useDocumentUpload({
      documentType: formData.type,
      maxSizeBytes: 25 * 1024 * 1024,
      onSuccess: (file, uploadType) => {
        setFormData((prev: DocumentFormData) => ({
          ...prev,
          pendingFile: file,
          uploadType,
          name: prev.name?.trim() ? prev.name : stripFileExtension(file.name),
        }));
      },
    });

  // Clear pending file when type changes
  useEffect(() => {
    updateFormData({ pendingFile: null, uploadType: null });
  }, [formData.type]);

  const onSelectDocument = (files: any[]): void => {
    if (!files?.length) return;
    validateAndProcessFile(files[0]);
  };

  const onSelectType = (type: string): void => {
    updateFormData({ type });
    clearError();
  };

  const removeAttachedFileFormData = (): void => {
    updateFormData({ pendingFile: null, uploadType: null, name: '' });
    clearError();
  };

  const isFormDisabled: boolean = !formData.type || !formData.pendingFile;

  const handleSubmit = async (): Promise<void> => {
    await onSave(formData);
    onClose();
  };

  return (
    <div className="offline-doc-form">
      <div className="offline-doc-form-body">
        <DocumentTypeSelector
          selectedType={formData.type}
          onSelectType={onSelectType}
        />
        <div className="offline-doc-form-divider" />
        <DocumentUploadFields
          formData={formData}
          errorMessage={errorMessage}
          onSelectDocument={onSelectDocument}
          onUpdateFormData={updateFormData}
          onClearFile={removeAttachedFileFormData}
          from={from}
        />
      </div>
      <div className="offline-doc-form-actions">
        <LoadingButton
          actionText="Save Document"
          onClick={handleSubmit}
          disabled={isFormDisabled}
          isSubmitting={saving}
        />
      </div>
    </div>
  );
};

export default ModernizedDocumentForm;
